// Parser for the annual equipment rate sheet the equipment manager supplies as .xlsx.
//
// Shared by the Admin > Equipment Rates tab and by the one-off migration
// generator, so the rows that land in the database are produced by exactly one
// set of rules no matter which path published them.
//
// Expected sheet shape (the 2026 sheet, and the format going forward):
//   Category | Equipment type/model | Class | Daily | Weekly | Monthly
//
// The header row is located by content rather than by index: the source file
// carries a title row above it, and that is the sort of thing that moves
// between revisions.

import * as XLSX from 'xlsx';

/** A rate row ready to POST to /equipment-rates/import. */
export interface ParsedRateRow {
  category: string;
  model: string;
  equipmentType: string;
  capacityRange: string | null;
  unit: string | null;
  dayRate: number | null;
  weekRate: number | null;
  monthRate: number | null;
  /** 1-based row number in the source sheet, for error reporting. */
  sourceRow: number;
}

export interface ParseIssue {
  sourceRow: number;
  message: string;
}

export interface ParsedRateSheet {
  rows: ParsedRateRow[];
  /** Rows that could not be imported at all - these are dropped. */
  errors: ParseIssue[];
  /** Rows that imported but look suspect - these are kept, and shown to the admin. */
  warnings: ParseIssue[];
}

// --- Class -> capacity_range + unit ------------------------------------------
//
// The sheet collapses capacity and unit into one "Class" cell ("600-799 CFM"),
// but the table and the picker keep them apart - RatePicker renders
// `{capacityRange} {unit}`. Splitting where it is unambiguous keeps the new
// sheet consistent with the rows already in the table.
//
// Only a clean "<number-ish> <unit>" pair is split. Anything else is kept whole
// in capacity_range with a null unit, which renders identically and never
// mangles the entries that are not really a capacity at all - "Lighting
// Equipment", "Ride On", "20-29 kW 3-Phase", "Up to 6.5in cut depth". Guessing
// at those would be worse than leaving them as the equipment manager typed them.

const UNIT_TOKENS: Record<string, string> = {
  cfm: 'CFM',   ft: 'Ft',         lb: 'Lb',          lbs: 'Lbs',
  ton: 'Ton',   tons: 'Tons',     in: 'In',          hp: 'HP',
  kw: 'kW',     gallon: 'Gallon', gallons: 'Gallons',
  cuft: 'CuFt', cuyd: 'CuYd',     amp: 'AMP',        amps: 'AMPS',
};

const NUMERIC_ISH = /^[0-9][0-9.,\-]*$/;

export function splitClass(raw: string): { capacityRange: string | null; unit: string | null } {
  const cleaned = raw.replace(/\s+/g, ' ').trim();
  if (!cleaned) return { capacityRange: null, unit: null };

  const parts = cleaned.split(' ');
  if (parts.length === 2 && NUMERIC_ISH.test(parts[0])) {
    const unit = UNIT_TOKENS[parts[1].toLowerCase()];
    if (unit) return { capacityRange: parts[0], unit };
  }
  return { capacityRange: cleaned, unit: null };
}

// --- Header location ---------------------------------------------------------

type Cell = string | number | boolean | null;

interface ColumnMap {
  category: number; model: number; klass: number;
  day: number; week: number; month: number;
}

// Matchers are anchored so they identify a heading rather than merely occur
// inside one. The model matcher earns the strictest treatment: an unanchored
// /model/ also matches "Model Year", and since the first match wins, an extra
// column ahead of the real one silently became the model for every row — years
// imported as equipment, with every other check passing.
//
// Anything a matcher does not recognise fails loudly in findHeader, which is
// the right trade: a heading we did not anticipate is a question for a human,
// not something to resolve by guessing at position.
const HEADER_MATCHERS: { key: keyof ColumnMap; test: (h: string) => boolean }[] = [
  { key: 'category', test: (h) => /^categor(y|ies)?$/.test(h) },
  {
    key: 'model',
    // "Model", "Equipment type", "Equipment type/model", "Model / equipment type".
    test: (h) => /^(model|equipment\s*type)(\s*[/&+]\s*(model|equipment\s*type))?$/.test(h),
  },
  { key: 'klass',    test: (h) => /^class(ification)?$/.test(h) },
  { key: 'day',      test: (h) => /^(dail(y)?|day)(\s*rate)?$/.test(h) },
  { key: 'week',     test: (h) => /^(week(ly)?)(\s*rate)?$/.test(h) },
  { key: 'month',    test: (h) => /^(month(ly)?)(\s*rate)?$/.test(h) },
];

/** Every column is required — see the note in findHeader on why none is guessed. */
const REQUIRED: { key: keyof ColumnMap; label: string }[] = [
  { key: 'category', label: 'Category' },
  { key: 'model',    label: 'Equipment type/model' },
  { key: 'klass',    label: 'Class' },
  { key: 'day',      label: 'Daily' },
  { key: 'week',     label: 'Weekly' },
  { key: 'month',    label: 'Monthly' },
];

function findHeader(rows: Cell[][]): { headerRow: number; cols: ColumnMap } {
  // Track the closest near-miss so the error can say what was actually wrong
  // rather than just "no header found".
  let best: { row: number; missing: string[] } | null = null;

  for (let r = 0; r < Math.min(rows.length, 20); r++) {
    const cells = (rows[r] ?? []).map((c) => (c == null ? '' : String(c).trim().toLowerCase()));

    // Collect EVERY match, not just the first. Taking the first silently
    // resolves an ambiguity that only a human can settle.
    const hits = new Map<keyof ColumnMap, number[]>();
    for (const { key, test } of HEADER_MATCHERS) {
      hits.set(key, cells.flatMap((c, i) => (c && test(c) ? [i] : [])));
    }

    const found: Partial<ColumnMap> = {};
    for (const [key, indexes] of hits) {
      if (indexes.length === 1) found[key] = indexes[0];
    }

    // Two columns answering to the same heading is ambiguous, and only counts
    // once we are confident this is the header row — otherwise a stray row
    // above it could abort the scan.
    const everyKeyPresent = REQUIRED.every(({ key }) => (hits.get(key)?.length ?? 0) > 0);
    if (everyKeyPresent) {
      const ambiguous = REQUIRED
        .filter(({ key }) => (hits.get(key)?.length ?? 0) > 1)
        .map(({ key, label }) => {
          const names = hits.get(key)!.map((i) => `"${cells[i]}"`).join(' and ');
          return `${label} (${names})`;
        });
      if (ambiguous.length) {
        throw new Error(
          `The header row (row ${r + 1}) has more than one column matching: ${ambiguous.join('; ')}. ` +
          'Rename or remove the extra column so each heading is unambiguous.'
        );
      }
    }

    // No positional fallback. Guessing "the column after Category" silently
    // maps the wrong data into the wrong field — a sheet with no Class column
    // reads Daily into capacity_range — and those rows sail through the preview
    // and replace the live catalogue. Every column is documented as required,
    // so a missing one fails loudly here instead.
    const missing = REQUIRED.filter(({ key }) => found[key] === undefined).map(({ label }) => label);

    if (missing.length === 0) {
      // Every heading must be its OWN column. A single cell reading
      // "Category / Equipment type/model" satisfies both matchers at the same
      // index, which would pass the required-header check and then read both
      // fields out of one column for every row.
      const byIndex = new Map<number, string[]>();
      for (const { key, label } of REQUIRED) {
        const i = found[key] as number;
        if (!byIndex.has(i)) byIndex.set(i, []);
        byIndex.get(i)!.push(label);
      }
      const collisions = [...byIndex.values()].filter((labels) => labels.length > 1);
      if (collisions.length) {
        throw new Error(
          `The header row (row ${r + 1}) maps more than one column to the same cell: ` +
          collisions.map((labels) => labels.join(' + ')).join('; ') +
          '. Each of Category, Equipment type/model, Class, Daily, Weekly and Monthly ' +
          'needs its own column.'
        );
      }
      return { headerRow: r, cols: found as ColumnMap };
    }
    // A row matching nothing is just a title row, not a broken header.
    if (missing.length < REQUIRED.length && (!best || missing.length < best.missing.length)) {
      best = { row: r + 1, missing };
    }
  }

  if (best) {
    throw new Error(
      `The header row (row ${best.row}) is missing: ${best.missing.join(', ')}. ` +
      'Expected columns: Category, Equipment type/model, Class, Daily, Weekly, Monthly.'
    );
  }
  throw new Error(
    'Could not find the header row. Expected columns: Category, Equipment type/model, Class, Daily, Weekly, Monthly.'
  );
}

// --- Values ------------------------------------------------------------------

function text(cell: Cell): string {
  if (cell == null) return '';
  // Excel stores bare model numbers ("725", "416") as numbers, not text.
  return String(cell).replace(/\s+/g, ' ').trim();
}

/**
 * A rate cell.
 *
 * A blank cell is a legitimately absent rate; a cell with something unreadable
 * in it is a typo. Collapsing both to null would let "$1O0" (letter O) publish
 * as a missing rate with no error, permanently replacing a real price with a
 * blank, so the two are kept apart.
 */
interface MoneyCell { value: number | null; invalid: boolean }

function money(cell: Cell): MoneyCell {
  if (cell == null) return { value: null, invalid: false };
  if (typeof cell === 'number') {
    return Number.isFinite(cell) ? { value: cell, invalid: false } : { value: null, invalid: true };
  }
  const raw = String(cell).trim();
  if (!raw) return { value: null, invalid: false };

  // Validate the whole thing BEFORE stripping anything. Stripping first and
  // trusting Number() is far too generous: it strips "10,00" into 1000 rather
  // than flagging a mis-grouped figure, and Number() happily reads "0x10" as
  // 16, "1e5" as 100000 and "Infinity" as infinite. Each of those publishes a
  // wrong rate from a plausible typo.
  //
  // Only whole-dollar grouping is accepted — either no commas at all, or
  // strictly 3-digit groups. A leading "-" is allowed through so the negative
  // check below can give a better message than "unreadable".
  //
  // Note this path handles TEXT cells only; Excel's numeric cells return early
  // above. So being strict here costs nothing on a well-formed sheet, and a
  // rejected row is reported in the preview rather than silently mis-read.
  if (!/^-?\$?\s*(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/.test(raw)) {
    return { value: null, invalid: true };
  }

  const n = Number(raw.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? { value: n, invalid: false } : { value: null, invalid: true };
}

// Column widths in equipment_rates, mirrored so the admin sees the problem in
// the preview rather than as a 500 from the insert.
const MAX = { category: 100, model: 100, equipmentType: 100, capacityRange: 50, unit: 20 } as const;

/**
 * Largest value a rate column holds: the rate columns are DECIMAL(10,2), so
 * eight digits ahead of the point and two behind.
 *
 * Mirrored here for the same reason the string widths are. A finite but
 * oversized figure — a mis-keyed 100000000 — passes every other check, then
 * fails at the INSERT and takes the whole transaction down with it, so nothing
 * publishes at all. Catching it here skips the one bad row, which is what the
 * preview says will happen.
 *
 * Scale is deliberately not policed: SQL Server rounds a third decimal place to
 * cents, which is the right thing to do to money.
 */
const MAX_RATE = 99_999_999.99;

export function parseRateSheet(buffer: ArrayBuffer): ParsedRateSheet {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error('The workbook has no sheets.');

  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null }) as Cell[][];
  const { headerRow, cols } = findHeader(grid);

  const rows: ParsedRateRow[] = [];
  const errors: ParseIssue[] = [];
  const warnings: ParseIssue[] = [];

  for (let r = headerRow + 1; r < grid.length; r++) {
    const raw = grid[r];
    if (!raw) continue;
    const sourceRow = r + 1; // 1-based, matches what Excel shows

    const category = text(raw[cols.category]);
    const model    = text(raw[cols.model]);
    const klass    = text(raw[cols.klass]);

    // A spacer row is one where every mapped cell is empty. Treating "no
    // category and no model" as the test skipped rows that still carried a
    // class or rates — so a row whose two identifying cells were cleared by
    // accident vanished with nothing reported, and a full replace then
    // deactivated the live entry it should have refreshed. Cells outside the
    // six mapped columns are ignored, so stray notes off to the side still
    // count as blank.
    const mapped = [cols.category, cols.model, cols.klass, cols.day, cols.week, cols.month];
    if (mapped.every((i) => text(raw[i]) === '')) continue;

    if (!category && !model) {
      const carried = klass ? `class "${klass}"` : 'rates';
      errors.push({
        sourceRow,
        message: `Row has ${carried} but no Category and no Equipment type/model`,
      });
      continue;
    }
    if (!category) {
      errors.push({ sourceRow, message: `Missing Category (model "${model}")` });
      continue;
    }
    if (!model) {
      errors.push({ sourceRow, message: `Missing Equipment type/model (category "${category}")` });
      continue;
    }

    const cells = {
      Daily:   money(raw[cols.day]),
      Weekly:  money(raw[cols.week]),
      Monthly: money(raw[cols.month]),
    };

    const unreadable = Object.entries(cells)
      .filter(([, c]) => c.invalid)
      .map(([label]) => label);
    if (unreadable.length) {
      errors.push({
        sourceRow,
        message: `"${category} / ${model}" has an unreadable ${unreadable.join(' and ')} rate`,
      });
      continue;
    }

    const dayRate   = cells.Daily.value;
    const weekRate  = cells.Weekly.value;
    const monthRate = cells.Monthly.value;

    if (dayRate == null && weekRate == null && monthRate == null) {
      errors.push({ sourceRow, message: `"${category} / ${model}" has no Daily, Weekly or Monthly rate` });
      continue;
    }

    // The DTO rejects a negative rate, which would 400 the whole publish.
    const negative = Object.entries(cells)
      .filter(([, c]) => c.value != null && c.value < 0)
      .map(([label]) => label);
    if (negative.length) {
      errors.push({
        sourceRow,
        message: `"${category} / ${model}" has a negative ${negative.join(' and ')} rate`,
      });
      continue;
    }

    // Over the column's range: skip the row here rather than let the INSERT
    // abort the transaction and publish nothing.
    const oversized = Object.entries(cells)
      .filter(([, c]) => c.value != null && c.value > MAX_RATE)
      .map(([label]) => label);
    if (oversized.length) {
      errors.push({
        sourceRow,
        message:
          `"${category} / ${model}" has a ${oversized.join(' and ')} rate above ` +
          `the ${MAX_RATE.toLocaleString('en-US')} maximum the column allows`,
      });
      continue;
    }

    const { capacityRange, unit } = splitClass(klass);

    // The category doubles as the equipment type. The sheet supplies a single
    // combined column, and the rows already in the table follow the same shape
    // (category "Scissor Lifts" alongside equipment_type "Scissor Lift"), so
    // this reproduces the existing display rather than inventing a taxonomy.
    const row: ParsedRateRow = {
      category, model, equipmentType: category,
      capacityRange, unit, dayRate, weekRate, monthRate, sourceRow,
    };

    // Over-width rows must be DROPPED, not merely reported. The publish posts
    // every row in `rows`, and the DTO's @MaxLength is validated per element —
    // so a single overlong value left in the array 400s the entire request and
    // nothing publishes, rather than being skipped as the preview promises.
    const tooLong = (Object.keys(MAX) as (keyof typeof MAX)[]).filter((field) => {
      const v = row[field];
      return typeof v === 'string' && v.length > MAX[field];
    });
    if (tooLong.length) {
      for (const field of tooLong) {
        const v = row[field] as string;
        errors.push({
          sourceRow,
          message: `${field} is ${v.length} characters, over the ${MAX[field]} the column allows: "${v}"`,
        });
      }
      continue;
    }

    // Ordering is not enforced - a genuine promo can invert it - but an
    // inverted rate is far more often a typo, so surface it.
    if (dayRate != null && weekRate != null && dayRate > weekRate) {
      warnings.push({ sourceRow, message: `"${category} / ${model}" daily $${dayRate} is above weekly $${weekRate}` });
    }
    if (weekRate != null && monthRate != null && weekRate > monthRate) {
      warnings.push({ sourceRow, message: `"${category} / ${model}" weekly $${weekRate} is above monthly $${monthRate}` });
    }

    rows.push(row);
  }

  // A duplicate category+model+class pair means two rows compete for the same
  // pick in the rate picker, with nothing to tell them apart.
  const seen = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.category}|${row.model}|${row.capacityRange ?? ''}`.toLowerCase();
    const first = seen.get(key);
    if (first !== undefined) {
      warnings.push({ sourceRow: row.sourceRow, message: `Duplicate of row ${first}: "${row.category} / ${row.model}"` });
    } else {
      seen.set(key, row.sourceRow);
    }
  }

  // Throw only when the sheet genuinely had nothing to read. If rows were found
  // and every one was rejected, return the errors instead — they say WHY, which
  // a bare "no rows found" would throw away, and the tab already renders them
  // and keeps the publish button disabled on an empty row set.
  if (!rows.length && !errors.length) {
    throw new Error('No rate rows found below the header row.');
  }
  return { rows, errors, warnings };
}

// --- What a publish would do to the rows already live --------------------------
//
// These mirror the deactivation predicate in EquipmentRatesService.bulkImport,
// so the counts the admin is shown before publishing are the counts that
// actually happen. They live here, out of the component, because this is the
// safety net that the trench-shoring rows needed and did not have.

/** The subset of an active rate row these calculations care about. */
export interface LiveRateRow {
  category: string;
  rateYear: number;
}

/**
 * Categories that are live today but absent from the uploaded sheet, with how
 * many rows each has.
 *
 * Under a full replace these vanish unless explicitly preserved. The equipment manager's
 * 2026 fleet sheet has no trench shoring in it, so publishing it blind would
 * have dropped seven components priced from a separate rental quote.
 */
export function orphanCategoryCounts(
  active: readonly LiveRateRow[],
  incoming: readonly { category: string }[],
): [string, number][] {
  const known = new Set(incoming.map((r) => r.category.trim().toLowerCase()));
  const counts = new Map<string, number>();
  for (const row of active) {
    if (known.has(row.category.trim().toLowerCase())) continue;
    counts.set(row.category, (counts.get(row.category) ?? 0) + 1);
  }
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/** How many live rows a publish with these settings would deactivate. */
export function countSuperseded(
  active: readonly LiveRateRow[],
  opts: { replaceAll: boolean; rateYear: number; preserveCategories: readonly string[] },
): number {
  const keep = new Set(opts.preserveCategories.map((c) => c.trim().toLowerCase()));
  return active.filter((row) => {
    if (keep.has(row.category.trim().toLowerCase())) return false;
    return opts.replaceAll || row.rateYear === opts.rateYear;
  }).length;
}
